<template>
  <div class="workspace-participants-management">
    <title-panel
      :button-label="t(addActionLabelKey)"
      :count="workspaceParticipants?.length ?? 0"
      :display-button="canManageWorkspaces"
      :on-button-click="onAddParticipantButtonClick"
      :title="t(titleKey)"
    ></title-panel>

    <user-select-modal
      ref="modalRef"
      modal-id="workspace-participants-modal"
      :ok-button-busy="isAddingWorkspaceParticipant"
      :placeholder="t(selectParticipantPlaceholderKey)"
      :search-users="searchCandidates"
      :selection-required-message="t(selectionRequiredMessageKey)"
      :title="t(addActionLabelKey)"
      @selected-user="handleUserSelected"
    />
  </div>

  <table-wrapper
    v-model:page-options="pageOptions"
    v-model:filters="filters"
    action-icon="delete"
    :action-icon-disabled="isRemoveDisabled"
    :action-icon-tooltip-text="deleteIconTooltipText"
    :display-action-icon="canManageWorkspaces"
    :fields="tableFields"
    :filter-configuration="filterConfiguration"
    :items="workspaceParticipants ?? []"
    :loading="isLoading"
    :mode="TableMode.CLIENT_SIDE"
    @action-icon-click="handleDeleteParticipantClick"
  >
  </table-wrapper>
</template>

<script lang="ts" setup>
  import { useWorkspaceParticipants } from '@/composables/api/workspaces/useWorkspaceParticipants';
  import { useRoles } from '@/composables/useRoles';
  import { IAdUserInternalFormat } from 'types';
  import { computed, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import {
    TableFieldsBuilder,
    TableFiltersBuilder,
    TableMode,
    useTableWrapper,
  } from '../TableWrapper';
  import UserSelectModal from '../UserSelectModal/UserSelectModal.vue';

  const props = defineProps<{
    workspaceId?: string;
    participantType: 'owner' | 'reviewer';
  }>();

  const { t } = useI18n();
  const { canManageWorkspaces } = useRoles();

  const {
    searchCandidates,
    workspaceParticipants,
    fetchWorkspaceParticipants,
    isAddingWorkspaceParticipant,
    addParticipantToWorkspace,
    removeParticipantFromWorkspace,
  } = useWorkspaceParticipants(props.participantType, props.workspaceId);

  const { pageOptions, filters, isLoading, fetch } =
    useTableWrapper<IAdUserInternalFormat>(
      TableMode.CLIENT_SIDE,
      'lastName',
      fetchWorkspaceParticipants,
    );

  const filterConfiguration = new TableFiltersBuilder<IAdUserInternalFormat>({
    translationKey: 'generic',
  })
    .addInputFilter('firstName', { width: '208px' })
    .addInputFilter('lastName', { width: '208px' })
    .addInputFilter('email', { width: '208px' })
    .build();

  const tableFields = computed(() =>
    new TableFieldsBuilder<IAdUserInternalFormat>({
      sortable: true,
      labelTranslationKey: 'generic',
    })
      .addField('firstName', { width: '36%' })
      .addField('lastName', { width: '36%' })
      .addField('email', { width: '34%' })
      .build(),
  );

  async function handleUserSelected(
    user: IAdUserInternalFormat,
  ): Promise<void> {
    await addParticipantToWorkspace(user.oid);
    await fetch();
  }

  async function handleDeleteParticipantClick(
    participant: IAdUserInternalFormat,
  ): Promise<void> {
    await removeParticipantFromWorkspace(participant.oid);
    await fetch();
  }

  const modalRef = ref<InstanceType<typeof UserSelectModal> | null>(null);

  function onAddParticipantButtonClick(): void {
    modalRef.value?.show();
  }

  const isLastOwner = computed(
    () =>
      props.participantType === 'owner' &&
      (workspaceParticipants.value?.length ?? 0) <= 1,
  );

  const isRemoveDisabled = computed(() => isLastOwner.value);

  const deleteIconTooltipText = computed(() =>
    isLastOwner.value
      ? t('workspaceOwnersManagement.cannotRemoveLastOwner')
      : '',
  );

  const translationPrefix = computed(() =>
    props.participantType === 'owner'
      ? 'workspaceOwnersManagement'
      : 'workspaceReviewersManagement',
  );

  const titleKey = computed(() =>
    props.participantType === 'owner'
      ? 'workspaces.owners'
      : 'workspaces.reviewers',
  );

  const addActionLabelKey = computed(
    () => `${translationPrefix.value}.add${capitalize(props.participantType)}`,
  );

  const selectParticipantPlaceholderKey = computed(
    () =>
      `${translationPrefix.value}.select${capitalize(props.participantType)}`,
  );

  const selectionRequiredMessageKey = computed(
    () =>
      `${translationPrefix.value}.errors.${props.participantType}SelectionRequired`,
  );

  function capitalize(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }
</script>

<style scoped lang="scss">
  .workspace-participants-management {
    margin-top: 2rem;
    border-top: 1px solid $border-color;
    padding-top: 2rem;
  }
</style>
